import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { prepacksApi } from '@/services/api'
import { normalizePrepack, titleSourceLabel } from './prepackCardModel'
import { repeatWarningLines, surfaceNamer } from './prepackRepeatsModel'

/**
 * Analyze, Regenerate title and Publish / Unpublish for one pack, as the
 * Library's drawer offers them (#984, #861; lifted for #1080). `invalidate()`
 * marks the organization's pack lists stale; `setOpenPack` is the drawer's
 * pack setter: Analyze opens the pack it answered with, a new title replaces
 * the open pack only while that pack is still open, and a lifecycle change
 * closes the drawer.
 */
export function usePackLifecycle({ slug, invalidate, setOpenPack }) {
  const queryClient = useQueryClient()
  const slugRef = useRef(slug)
  useEffect(() => { slugRef.current = slug }, [slug])

  const analyzeMutation = useMutation({ mutationFn: (id) => prepacksApi.analyzePrepack(slug, id), onSuccess: (pack) => { setOpenPack(normalizePrepack(pack)); invalidate() }, onError: (error) => toast.error(error?.response?.data?.error || 'Analysis failed') })
  const publishMutation = useMutation({ mutationFn: (id) => prepacksApi.publishPrepack(slug, id), onSuccess: invalidate })
  const unpublishMutation = useMutation({ mutationFn: (id) => prepacksApi.unpublishPrepack(slug, id), onSuccess: invalidate })
  // A new title for the pack, its picks kept (#984). The answer belongs to
  // the organization and pack that asked: it refreshes that library, and
  // updates the drawer only while the same pack is still open there.
  const regenerateMutation = useMutation({
    mutationFn: ({ pack, slug: requestSlug }) => prepacksApi.regenerateTitle(requestSlug, pack.id, pack.updated_at ? { updated_at: pack.updated_at } : {}),
    onSuccess: (pack, { slug: requestSlug }) => {
      queryClient.invalidateQueries({ queryKey: ['prepacks', 'library', requestSlug] })
      if (slugRef.current !== requestSlug) return
      setOpenPack((current) => (current?.id === pack.id ? normalizePrepack(pack) : current))
      // A title not written by AI came from the fallbacks: say so, since the
      // operator asked for an AI one.
      const fallback = titleSourceLabel(pack)
      toast.success(`New title: ${pack.title}`, pack.title_source === 'ai' || !fallback ? undefined : { description: `No AI title was available or unique, so this is the ${fallback.toLowerCase()}.` })
    },
    onError: (error, { slug: requestSlug }) => {
      if (slugRef.current === requestSlug) toast.error(error?.response?.data?.error || 'Could not regenerate the title')
    },
  })

  const changeLifecycle = async (pack, action) => {
    const publish = action ? action === 'publish' : pack.status !== 'published'
    try {
      const saved = await (publish ? publishMutation : unpublishMutation).mutateAsync(pack.id)
      toast.success(publish ? 'Prepack published' : 'Prepack moved to draft')
      // A publish answers with what the pack now puts past a repeat limit: a warning, never a refusal.
      if (publish && saved?.warnings?.length) {
        const surfaces = await queryClient.fetchQuery({ queryKey: ['prepacks', 'surfaces', slug], queryFn: () => prepacksApi.listSurfaces(slug), staleTime: 60_000 }).catch(() => null)
        const lines = repeatWarningLines(saved, surfaceNamer(surfaces?.surfaces))
        if (lines.length) toast.warning('Past the repeat limits', { description: lines.join('. ') })
      }
      setOpenPack(null)
    } catch (error) {
      toast.error(error?.response?.data?.error || `Failed to ${publish ? 'publish' : 'unpublish'} prepack`)
    }
  }

  return {
    analyze: (id) => analyzeMutation.mutate(id),
    regenerate: (pack) => regenerateMutation.mutate({ pack, slug }),
    changeLifecycle,
    pending: {
      analyze: analyzeMutation.isPending,
      regenerate: regenerateMutation.isPending,
      lifecycle: publishMutation.isPending || unpublishMutation.isPending,
    },
  }
}
